import { useParams } from 'next/navigation';
import { useMemo } from 'react';

// Import des fichiers de traduction
import enUS from '../i18n/locales/en-US.json';
import frFR from '../i18n/locales/fr-FR.json';

const translations = {
  'en-US': enUS,
  'fr-FR': frFR,
};

type TranslationOptions = {
  returnObjects?: boolean;
  defaultValue?: any;
  [key: string]: any;
};

export type SupportedLanguages = 'en-US' | 'fr-FR';

// Fonction de traduction côté serveur
export function getTranslation(locale: string) {
  return (key: string, options?: TranslationOptions) => {
    // Silently handle empty strings (intentional fallbacks)
    if (key === '') {
      return options?.defaultValue || '';
    }
    // Warn only for undefined/null (actual bugs)
    if (key === undefined || key === null) {
      console.warn(`[getTranslation] called with undefined key - options: ${JSON.stringify(options)}`);
      return options?.defaultValue || '';
    }
    const keys = key.split('/');
    let translation = translations[locale] || translations['en-US'];

    for (const k of keys) {
      if (translation && translation[k]) {
        translation = translation[k];
      } else {
        return options?.defaultValue || key;
      }
    }

    // Gestion de returnObjects
    if (options?.returnObjects) {
      return translation;
    }

    if (typeof translation === 'string') {
      return interpolate(translation, options);
    }

    return translation;
  };
}

// Fonction d'interpolation
function interpolate(template: string, options?: TranslationOptions): string {
  if (!options) return template;

  // Remplace {{ key }} par la valeur correspondante
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
    // Vérifie si l'option existe et a une valeur
    if (options[key] !== undefined) {
      return String(options[key]);
    }
    // Retourne le match original si la clé n'est pas trouvée
    return match;
  });
}

// Hook côté client
export function useTranslation(initialLocale?: string) {
  const params = useParams();
  const locale = initialLocale || (params?.locale as string) || 'en-US';

  const t = useMemo(() => {
    return getTranslation(locale);
  }, [locale]);

  return { t, locale };
}
